import {
  type OasDocument3XExtended,
  type OasWebPageTreeNode,
  isNonNullable,
  isOasWebMenuNode,
  isOasWebPageApiTable,
  isOasWebTabsPage,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import type { PathsWithName } from 'components/DeviceView/deviceViewTypes';
import type { RoutesWithApiEndpoint } from 'containers/App/routing/RoutesWithApiEndpoint';
import { array, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/lib/function';
import { getXTableOas } from 'utils/oas/getXTableOas';

const getPathsFromNodes = (
  nodes: OasWebPageTreeNode[],
  routesWithApiEndpoint: RoutesWithApiEndpoint,
  oas: OasDocument3XExtended,
): PathsWithName => {
  return nodes.reduce<PathsWithName>((acc, node) => {
    if (isOasWebMenuNode(node)) {
      Object.assign(
        acc,
        getPathsFromNodes(node.submenu, routesWithApiEndpoint, oas),
      );
    } else if (isOasWebTabsPage(node)) {
      Object.assign(
        acc,
        getPathsFromNodes(node.tabs, routesWithApiEndpoint, oas),
      );
    } else if (
      isOasWebPageApiTable(node) &&
      oas.paths?.[node.apiTable] !== undefined
    ) {
      pipe(
        getXTableOas(oas, node.apiTable),
        option.fromNullable,
        option.chainNullableK(prop('webPath')),
        option.map((webPath) => {
          acc[webPath] =
            oas.paths?.[node.apiTable]?.post?.summary ?? node.title;
        }),
      );
    }
    return acc;
  }, {});
};

export const getApiTablePathsWithName = (
  routesWithApiEndpoint: RoutesWithApiEndpoint,
  oas: OasDocument3XExtended | null,
): PathsWithName => {
  const xWebPages = oas?.['x-webPages'];
  return pipe(
    xWebPages,
    option.fromPredicate(isNonNullable),
    option.map(
      flow(
        Object.values,
        array.reduce(
          {},
          (acc: PathsWithName, nodes: OasWebPageTreeNode[]): PathsWithName => {
            Object.assign(
              acc,
              getPathsFromNodes(nodes, routesWithApiEndpoint, oas!),
            );
            return acc;
          },
        ),
      ),
    ),
    option.getOrElse(() => ({})),
  );
};
